// The yearly IRS settlement (liquidação) for someone with employment income only, filing on their own.
//
//   1. Exempt part (IRS Jovem) = exemption % × gross employment income, capped at 55 × IAS.
//   2. Employment deduction (art. 25.º) = the larger of 4 104 €, 8,54 × IAS, or the social security paid.
//      It comes off the part that isn't exempt.
//   3. The exempt income still counts when working out the rate ("englobamento para determinação da taxa"):
//      rate = tax on (all income − deduction) ÷ (all income − deduction).
//   4. Tax = rate × (non-exempt income − deduction) − deductions from the tax (health, general expenses, …).
//   5. Withholding already paid during the year is subtracted: a positive result is a refund.
//
// This reproduces the OCC "Guia prático IRS Jovem" simulations to the cent (see tests). The mínimo de existência is
// applied in a simplified, conservative way.

import { r2 } from '../money.js';
import { irsJovemCap, specificDeductionAmount } from './params.js';

// Tax on taxable income with the brackets of art. 68.º. As in n.º 2 of that article, income above the first bracket is
// split in two: up to the previous limit it pays that bracket's average rate (as published, 3 decimals), the rest pays
// the normal rate of its own bracket. Brackets typed without an average rate get the exact one.
export function bracketTax(income, brackets) {
  if (!(income > 0)) return 0;
  let lower = 0;
  let taxAtLower = 0;
  for (const b of brackets) {
    const upper = b.upTo === null || b.upTo === undefined ? Infinity : b.upTo;
    if (income <= upper) return taxAtLower + (income - lower) * (b.rate / 100);
    const exactAtUpper = taxAtLower + (upper - lower) * (b.rate / 100);
    taxAtLower = typeof b.avg === 'number' ? upper * (b.avg / 100) : exactAtUpper;
    lower = upper;
  }
  return taxAtLower;
}

export function annualSettlement({ params, gross, socialSecurity = 0, jovemPct = 0, personalDeductions = 250, withheld = 0 }) {
  const g = Math.max(0, gross || 0);
  const cap = irsJovemCap(params);
  const exempt = r2(Math.min(g * (jovemPct / 100), cap));
  const nonExempt = r2(g - exempt);
  const deduction = r2(Math.min(g, Math.max(specificDeductionAmount(params), socialSecurity || 0)));
  const rcTotal = Math.max(0, g - deduction);
  const rcTaxable = r2(Math.max(0, nonExempt - deduction));
  const totalTax = bracketTax(rcTotal, params.brackets);
  const rate = rcTotal > 0 ? totalTax / rcTotal : 0;
  const coleta = rcTaxable * rate;
  let tax = Math.max(0, coleta - Math.max(0, personalDeductions || 0));
  // Mínimo de existência: tax can't leave someone with less than the reference amount (simplified)
  const me = params.minimoExistencia || 0;
  if (me && g - tax < me) tax = Math.max(0, g - me);
  tax = r2(tax);
  return {
    gross: r2(g),
    exempt,
    nonExempt,
    cap,
    deduction,
    rcTotal: r2(rcTotal),
    rcTaxable,
    rate: Math.round(rate * 1e6) / 1e4, // % with 4 decimals
    coleta: r2(coleta),
    personalDeductions: r2(personalDeductions || 0),
    tax,
    withheld: r2(withheld),
    refund: r2(withheld - tax),
  };
}
